import { createApi } from '@reduxjs/toolkit/dist/query/react';

import {
  getBaseQuery,
  getListProvidesTags,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import * as endpoints from '../endpoints';
import { REDUX_CACHE, HTTP_METHOD, SUBSCRIPTIONS } from '../constants';
import { ISubscriptionPlan } from '../../../types/interfaces/subscription-plan';
import { ISubscriptionRestriction } from '../../../types/interfaces/subscription-restriction';
import { ISubscriptionPlanPrice } from '../../../types/interfaces/subscription-plan-price';

export const subscriptionsApi = createApi({
  reducerPath: 'subscriptionsApi',
  tagTypes: [
    SUBSCRIPTIONS.PLANS,
    SUBSCRIPTIONS.RESTRICTIONS,
    SUBSCRIPTIONS.PLAN_PRICES,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery('/admin/subscriptions'),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getPlansApi: builder.query<ISubscriptionPlan[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Subscriptions.PLANS,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, SUBSCRIPTIONS.PLANS),
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutSubscriptionPlanApi: builder.mutation<
      void,
      { id: string; body: ISubscriptionPlan }
    >({
      query({ id, body }) {
        return {
          url: endpoints.Subscriptions.PLANS + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: SUBSCRIPTIONS.PLANS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: SUBSCRIPTIONS.PLANS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Rule'),
    }),
    deleteSubscriptionPlanApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.Subscriptions.PLANS}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: SUBSCRIPTIONS.PLANS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
    getSubscriptionRestrictionsApi: builder.query<
      ISubscriptionRestriction[],
      void
    >({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Subscriptions.RESTRICTIONS,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, SUBSCRIPTIONS.RESTRICTIONS),
      ...TransformedErrorResponseWithNotification(),
    }),

    postPutSubscriptionRestrictionApi: builder.mutation<
      void,
      { id: string; body: ISubscriptionRestriction }
    >({
      query({ id, body }) {
        return {
          url: endpoints.Subscriptions.RESTRICTIONS + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: SUBSCRIPTIONS.RESTRICTIONS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: SUBSCRIPTIONS.RESTRICTIONS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
    deleteSubscriptionRestrictionApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.Subscriptions.RESTRICTIONS}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: SUBSCRIPTIONS.RESTRICTIONS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),

    getSubscriptionPlanPricesApi: builder.query<ISubscriptionPlanPrice[], void>(
      {
        keepUnusedDataFor: 3600,
        query() {
          return {
            url: endpoints.Subscriptions.PLAN_PRICES,
            method: HTTP_METHOD.GET,
          };
        },
        providesTags: (result) =>
          getListProvidesTags(result, SUBSCRIPTIONS.PLAN_PRICES),
        ...TransformedErrorResponseWithNotification(),
      },
    ),
    postPutSubscriptionPlanPriceApi: builder.mutation<
      void,
      { id: string; body: ISubscriptionPlanPrice }
    >({
      query({ id, body }) {
        return {
          url: endpoints.Subscriptions.PLAN_PRICES + (id ? `/${id}` : ''),
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: SUBSCRIPTIONS.PLANS,
            },
            {
              type: SUBSCRIPTIONS.PLAN_PRICES,
              id: REDUX_CACHE.LIST,
            },
            {
              type: SUBSCRIPTIONS.PLAN_PRICES,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Rule'),
    }),
    deleteSubscriptionPlanPriceApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.Subscriptions.PLAN_PRICES}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: SUBSCRIPTIONS.PLANS,
            },
            {
              type: SUBSCRIPTIONS.PLAN_PRICES,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification(),
    }),
  }),
});

export const {
  useGetPlansApiQuery: useGetPlans,
  useLazyGetPlansApiQuery: useLazyGetPlans,
  useDeleteSubscriptionPlanApiMutation: useDeleteSubscriptionPlan,
  usePostPutSubscriptionPlanApiMutation: usePostPutSubscriptionPlan,

  useGetSubscriptionPlanPricesApiQuery: useGetSubscriptionPlanPrices,
  useDeleteSubscriptionPlanPriceApiMutation: useDeleteSubscriptionPlanPrice,
  usePostPutSubscriptionPlanPriceApiMutation: usePostPutSubscriptionPlanPrice,

  useGetSubscriptionRestrictionsApiQuery: useGetRestrictions,
  useDeleteSubscriptionRestrictionApiMutation: useDeleteSubscriptionRestriction,
  usePostPutSubscriptionRestrictionApiMutation:
    usePostPutSubscriptionRestriction,
} = subscriptionsApi;
